/**
 * The citation review wizard: one finding at a time.
 *
 * Deliberately not a list. Eleven citation problems on one screen invites scanning and
 * fixing none, so the queue hands over one finding with its checks embedded, and the
 * problem sits beside the verse it is about.
 */

import type { JSX } from 'react'

/**
 * The study workspace: the cards a reader works their own corpus in.
 *
 * Separate from `cards.tsx`, which is the *text* — passages, evidence, the
 * lexicon. That file is what the product is for; this one is where the reader
 * keeps what they found. They share the output window and its stack, because a
 * finding is a passage-shaped thing and belongs where passages live, but they
 * are different jobs and a single thousand-line file would hide that.
 *
 * Two rules from the design shape everything here:
 *
 * * **Cards are data, never closures.** Every card in the stack is a plain
 *   value — `{ kind: 'finding', id }`, never `{ onSave: () => … }` — so the
 *   stack stays serialisable and navigation is not coupled to whoever pushed
 *   it. Anything a card needs from React state arrives as a prop on the window.
 * * **Filtering, sorting and paging happen on the server.** A corpus grows
 *   without limit; a page that ships every row and hides most of them gets
 *   slower every time the reader does good work. Sorting one page in the
 *   browser would be worse still — it would reorder the window while claiming
 *   to have sorted the list.
 */

import { useState } from 'react'
import type {
  CitationCheck,
  FindingDetail,
  LostReference,
  MovedSource,
  ReviewQueue,
} from '../account'
import {
  acceptCheck,
  markSourceSeen,
  reviewQueue,
} from '../account'
import type { Card } from '../cards'
import { t } from '../i18n'

import { Failure, Loading, useLoaded } from './cardDisplay'

// ---------------------------------------------------------------------------
// The review wizard
// ---------------------------------------------------------------------------

/** Nothing to review. Reached two ways — a genuinely empty queue, and the clamp below. */
function EmptyQueue({ beside }: { beside: JSX.Element | null }): JSX.Element {
  return (
    <article>
      <h2 className="card__kind">{t('review.title')}</h2>
      <p className="muted">{t('review.empty')}</p>
      {beside}
    </article>
  )
}

/**
 * References pointing at findings that no longer exist.
 *
 * A list, where the citation queue above is a wizard, and the difference is the
 * kind of problem. A citation problem is a judgement about text that needs the
 * verse beside it and can be *accepted*; a dangling reference has nothing to
 * look at and nothing to accept — the reader either edits the finding or leaves
 * the record standing. Giving it an "accept" button would be offering an action
 * that does not exist.
 */
function DanglingReferences({
  entries,
  onOpen,
}: {
  entries: DanglingEntry[]
  onOpen: (card: Card) => void
}): JSX.Element | null {
  if (entries.length === 0) return null

  return (
    <section className="dangling">
      <h3>{t('review.dangling')}</h3>
      <p className="muted">{t('review.danglingNote')}</p>
      <ul className="claims">
        {entries.map((entry) => (
          <li key={`${entry.kind}:${entry.id}`} className="claim">
            <button
              type="button"
              className="link"
              // A thread has no read-only card, only its editor — which is the
              // right destination anyway: the fix for a dangling reference is
              // to edit the record that holds it.
              onClick={() =>
                onOpen(
                  entry.kind === 'finding'
                    ? { kind: 'finding', id: entry.id }
                    : { kind: 'threadEditor', id: entry.id },
                )
              }
            >
              {entry.title}
            </button>
            {entry.unresolved.map((reference) => (
              <span
                key={`${reference.field}:${reference.target}`}
                className="muted reference--lost"
              >
                {' '}
                {t('review.danglingIn', {
                  field: reference.field,
                  target: reference.target,
                })}
              </span>
            ))}
          </li>
        ))}
      </ul>
    </section>
  )
}

/** One row of the list above — a finding or a thread, flattened to what it needs. */
interface DanglingEntry {
  kind: 'finding' | 'thread'
  id: number
  title: string
  unresolved: LostReference[]
}

function danglingEntries(queue: ReviewQueue): DanglingEntry[] {
  return [
    ...queue.dangling.findings.map((entry) => ({ ...entry, kind: 'finding' as const })),
    ...queue.dangling.threads.map((entry) => ({ ...entry, kind: 'thread' as const })),
  ]
}

/**
 * The two things beside the wizard, which are not steps in it.
 *
 * Both are shown whether or not the queue has anything in it — a reader with no
 * citation problems can still have a dangling reference or a source that moved,
 * and an empty wizard that hid them would report "nothing needs a look" while
 * two things did.
 */
function BesideTheQueue({
  queue,
  onOpen,
  onSeen,
}: {
  queue: ReviewQueue
  onOpen: (card: Card) => void
  onSeen: () => void
}): JSX.Element {
  return (
    <>
      <DanglingReferences entries={danglingEntries(queue)} onOpen={onOpen} />
      <MovedSources entries={queue.moved_sources} onOpen={onOpen} onSeen={onSeen} />
    </>
  )
}

/**
 * Adopted claims whose original has since been revised.
 *
 * Beside the dangling block rather than in the wizard, and for the same reason:
 * there is nothing here to accept or fix. The reader's own claim is untouched —
 * what moved is the reading they based it on — so the only actions are to look
 * at the original and to say they have looked.
 */
function MovedSources({
  entries,
  onOpen,
  onSeen,
}: {
  entries: MovedSource[]
  onOpen: (card: Card) => void
  onSeen: () => void
}): JSX.Element | null {
  if (entries.length === 0) return null

  async function seen(id: number) {
    await markSourceSeen(id)
    onSeen()
  }

  return (
    <section className="moved-sources">
      <h3>{t('review.moved')}</h3>
      <p className="muted">{t('review.movedNote')}</p>
      <ul className="claims">
        {entries.map((entry) => (
          <li key={entry.id} className="claim">
            <p className="claim__text">
              <button
                type="button"
                className="link"
                onClick={() => onOpen({ kind: 'finding', id: entry.id })}
              >
                {entry.title}
              </button>
            </p>
            <p className="claim__meta muted">
              {t('review.movedFrom', { author: entry.author })}
              <button
                type="button"
                className="link"
                onClick={() => onOpen({ kind: 'finding', id: entry.original_id })}
              >
                {t('review.movedOpen')}
              </button>
              <button type="button" className="link" onClick={() => void seen(entry.id)}>
                {t('review.movedSeen')}
              </button>
            </p>
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * One citation problem: what is wrong, and the verse it is wrong about.
 *
 * The passage link is the point of the whole wizard — the problem and the text it concerns
 * have to sit side by side, or the reader is deciding without looking.
 */
function CheckProblem({
  check,
  onOpen,
}: {
  check: CitationCheck
  onOpen: (card: Card) => void
}): JSX.Element {
  return (
    <div className="check check--problem">
      <p>
        <span className="badge badge--problem">{t(`citations.verdict.${check.verdict}`)}</span>{' '}
        <strong>{check.subject}</strong>
      </p>
      <p className="muted">{check.detail}</p>
      {check.kind === 'reference' && (
        <button
          type="button"
          className="link"
          onClick={() => onOpen({ kind: 'passage', reference: check.subject })}
        >
          {t('citations.openPassage')}
        </button>
      )}
    </div>
  )
}

/**
 * Fix it, accept it, or leave it for later.
 *
 * `fix` is disabled for a borrowed finding: an imported finding is read-only, and offering
 * an edit that the server will refuse is worse than not offering it.
 */
function ReviewActions({
  finding,
  hasNext,
  onFix,
  onAccept,
  onSkip,
}: {
  finding: FindingDetail
  hasNext: boolean
  onFix: () => void
  onAccept: () => void
  onSkip: () => void
}): JSX.Element {
  return (
    <div className="form__row">
      <button type="button" onClick={onFix} disabled={finding.imported_from !== ''}>
        {t('review.fix')}
      </button>
      <button type="button" onClick={onAccept}>
        {t('review.accept')}
      </button>
      <button type="button" className="link" disabled={!hasNext} onClick={onSkip}>
        {t('review.skip')}
      </button>
    </div>
  )
}

/**
 * The citation review queue, one finding at a time.
 *
 * A wizard rather than a list because the work is *per finding*: read the
 * problem, look at the verse beside it, then fix it, accept it, or leave it
 * for later. A list of eleven problems invites scanning and fixing none.
 */
export function ReviewCard({
  corpus,
  onOpen,
  onStudyChanged,
}: {
  corpus: string | null
  onOpen: (card: Card) => void
  onStudyChanged?: () => void
}): JSX.Element {
  const { state, reload } = useLoaded(() => reviewQueue(corpus ?? undefined), `review:${corpus}`)
  const [position, setPosition] = useState(0)

  if (state.status === 'loading') return <Loading />
  if (state.status === 'error') return <Failure message={state.message} />

  const queue = state.data.findings
  // Working inside a card does not move the stack, so the header only learns
  // that the queue shrank if the card says so.
  const refresh = () => {
    reload()
    onStudyChanged?.()
  }
  const beside = <BesideTheQueue queue={state.data} onOpen={onOpen} onSeen={refresh} />
  if (queue.length === 0) return <EmptyQueue beside={beside} />

  const index = Math.min(position, queue.length - 1)
  // The clamp and the empty-queue return above together guarantee an element, but an index
  // read is `T | undefined` to the compiler regardless. `at()` says the same thing and lets
  // the empty view stand in for the impossible case — which beats a non-null assertion,
  // because an assertion is a claim the compiler is told to accept rather than check.
  const finding = queue.at(index)
  if (finding === undefined) return <EmptyQueue beside={beside} />

  const problems = finding.checks.filter((check) => check.is_actionable && !check.is_accepted)
  // Bound out here because a hoisted function declaration does not inherit the narrowing
  // above, so `finding` reads as possibly-undefined inside acceptAll even though the guard
  // has already returned.
  const findingId = finding.id

  async function acceptAll() {
    for (const check of problems) await acceptCheck(findingId, check.id)
    refresh()
  }

  return (
    <article>
      <header className="card__head">
        <h2 className="card__kind">{t('review.title')}</h2>
        <span className="muted">
          {t('review.position', { current: index + 1, total: state.data.total })}
        </span>
      </header>

      <p className="question">{finding.title}</p>

      {problems.map((check) => (
        <CheckProblem key={check.id} check={check} onOpen={onOpen} />
      ))}

      <ReviewActions
        finding={finding}
        hasNext={index + 1 < queue.length}
        onFix={() => onOpen({ kind: 'findingEditor', id: finding.id })}
        onAccept={() => void acceptAll()}
        onSkip={() => setPosition(index + 1)}
      />

      {finding.imported_from !== '' && (
        <p className="muted">{t('review.importedNote', { author: finding.imported_from })}</p>
      )}

      {beside}
    </article>
  )
}

